import { nanoid } from 'nanoid';
import * as React from 'react';
import { FormGroup, FormControl } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import Select from 'react-select';
import type {
	SelectComponentsConfig,
	OptionProps,
	OnChangeValue,
	Theme,
	StylesConfig,
	GroupBase,
} from 'react-select';
import { change, touch, formValueSelector } from 'redux-form';
import type { WrappedFieldProps } from 'redux-form';

import type { RootState } from 'af-reducers';

import { UNIQUE_ID_SIZE } from 'ab-common/constants/value';

import Label from 'af-components/LockedValue/Label';

export interface OwnProps<T> {
	addonComponent?: () => React.JSX.Element | string;
	className?: string;
	containerClassName?: string;
	disableErrorMessage?: boolean;
	getMultiValueLabelClassName?: (data: T) => string;
	getOptionLabel?: (option: T) => string;
	getOptionValue?: (option: T) => string;
	isDisabled?: boolean;
	label?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	MultiValueLabel?: React.ComponentType<any>;
	onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
	onChange?: (items: T[]) => void;
	onValueChange?: (selectedOption: OnChangeValue<T, true>) => Promise<void>;
	Option?: React.ComponentType<OptionProps<T, true>>;
	options: T[];
	menuPlacement?: 'auto' | 'top' | 'bottom';
	placeholder?: string;
	valueKey?: string;
	fixed?: boolean;
	optionEqual: (optionA: T, optionB: T) => boolean;
}

type ConnectOwnProps<T> = OwnProps<T> & WrappedFieldProps;

// Define mapStateToProps and connector early to construct the Props type sequentially
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapStateToProps = (state: RootState, props: ConnectOwnProps<any>) => {
	const { meta: { form }, input: { name } } = props;
	return { formValue: formValueSelector(form)(state, name) };
};

const connector = connect(mapStateToProps, { changeField: change, touchField: touch });

type Props<T> = ConnectOwnProps<T> & ConnectedProps<typeof connector>;

interface DropdownStyle {
	top: number | 'auto';
	bottom: number | 'auto';
	left: number;
	width: number;
}

interface State {
	id: string;
	dropdownStyle: DropdownStyle | undefined;
}

interface CustomThemeColors {
	primary25: string;
}

const getTheme = (theme: Theme): Theme => ({
	...theme,
	borderRadius: 2,
	colors: {
		...theme.colors,
		...({
			primary25: '#FEF4E9',
		} as typeof theme.colors & CustomThemeColors),
		primary: '#FFA726',
	},
});

const customStyles: StylesConfig = {
	control: (base) => ({
		...base,
		boxShadow: '0 !important',
		'&:focus': { borderColor: '#FFA726' },
		'&:active': { borderColor: '#FFA726' },
	}),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class MultiTagSelect<OptionType = any> extends React.PureComponent<Props<OptionType>, State> {
	state: State = {
		id: nanoid(UNIQUE_ID_SIZE),
		dropdownStyle: undefined,
	};

	// Changed internal parameter to true since isMulti={true} is explicitly applied below
	components: SelectComponentsConfig<OptionType, true, GroupBase<OptionType>> = {
		IndicatorSeparator: null,
		DropdownIndicator: () => <span className="icon-down" />,
		ClearIndicator: (props) => (
			<div {...props.innerProps}>
				<span className="icon-close" />
			</div>
		),
		Option: this.props.Option ?? undefined,
		MultiValueLabel: this.props.MultiValueLabel ?
			(props) => {
				const { MultiValueLabel } = this.props;
				if (!MultiValueLabel) throw new Error('Label not defined');
				return <MultiValueLabel {...props} onRemove={this.onRemove.bind(this, props.data)} />;
			} : undefined,
		MultiValueRemove: this.props.MultiValueLabel ? () => null : undefined,
	};

	onRemove = (data: OptionType, event: React.MouseEvent<MouseEvent>) => {
		event.stopPropagation();
		const { formValue, optionEqual, changeField, meta: { form }, input: { name } } = this.props;
		const newValue = (formValue as OptionType[]).filter((_value) => !optionEqual(_value, data));
		changeField?.(form, name, newValue);
	};

	onChange = (items: OnChangeValue<OptionType, true>) => {
		const { onChange, meta: { form }, input: { name }, changeField } = this.props;
		const arrayItems = items ? [...items] : [];
		changeField?.(form, name, arrayItems);

		if (onChange) {
			onChange(arrayItems);
		}
	};

	onOpen = () => {
		const { fixed } = this.props;
		if (!fixed) return;

		const { id } = this.state;
		const {
			x: inputPositionX = 0,
			bottom: inputBottom = 0,
			width: inputWidth = 0,
		} = document.getElementById(id)?.getClientRects?.()?.[0] ?? {};

		this.setState(() => ({
			dropdownStyle: {
				top: inputBottom,
				bottom: 'auto',
				left: inputPositionX,
				width: inputWidth,
			},
		}));
	};

	onBlur = (event: React.FocusEvent<HTMLElement>) => {
		const { onBlur, touchField, meta: { form }, input: { name } } = this.props;
		touchField(form, name);
		if (onBlur) {
			onBlur(event);
		}
	};

	render() {
		const {
			placeholder,
			options = [],                 // Default handled here
			label,
			containerClassName,
			className = '',               // Default handled here
			getOptionValue,
			getOptionLabel,
			disableErrorMessage = false,  // Default handled here
			meta: { error, warning, touched },
			addonComponent,
			isDisabled = false,           // Default handled here
			menuPlacement = 'auto',       // Default handled here
			fixed,
			formValue,
		} = this.props;
		const { id, dropdownStyle } = this.state;

		let formGroupClassName = 'react-select-container';
		formGroupClassName = containerClassName ? `${formGroupClassName} ${containerClassName}` : formGroupClassName;
		formGroupClassName = addonComponent ? `${formGroupClassName} react-select-container--with-addon` : formGroupClassName;

		let selectClassName = 'react-select-field react-select-field--multi-tag';
		selectClassName = className ? `${selectClassName} ${className}` : selectClassName;
		selectClassName = touched && error ? `${selectClassName} react-select-field--error` : selectClassName;
		selectClassName = touched && fixed ? `${selectClassName} react-select-field--fixed` : selectClassName;

		const styles = fixed ? { ...customStyles, menu: (base) => ({ ...base, ...dropdownStyle }) } : customStyles;

		return (
			<FormGroup className={formGroupClassName}>
				{label && <Label label={label} withMargin={true} />}
				<Select
					className={selectClassName}
					components={this.components}
					getOptionLabel={getOptionLabel}
					getOptionValue={getOptionValue}
					id={id}
					isClearable={!isDisabled}
					isDisabled={isDisabled}
					isMulti={true}
					isSearchable={false}
					menuPlacement={menuPlacement}
					onBlur={this.onBlur}
					onChange={this.onChange}
					onMenuOpen={this.onOpen}
					options={options}
					placeholder={placeholder}
					styles={styles}
					theme={getTheme}
					value={formValue}
				/>
				{addonComponent?.()}
				<FormControl.Feedback />
				{(!disableErrorMessage) &&
					((touched && error && <span className="help-block"><span className="icon-info" /> {error}</span>) ||
						(touched && warning && <span className="help-block text-orange"><span className="icon-info" /> {warning}</span>))
				}
			</FormGroup>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default connector(MultiTagSelect) as React.FunctionComponent<WrappedFieldProps & OwnProps<any>>;
